import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { formatDMY, currentMonthISO } from "@/lib/dateFormat";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Handshake, Plus, Pencil, Trash2, Copy, ExternalLink, Download, CheckCircle2, X, DollarSign, Users, Send, Image as ImageIcon, Upload, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";

// The public URL the affiliate shares. When PUBLIC_BASE_URL is not set on the
// frontend .env, fall back to the WordPress landing (production expected).
const REF_BASE = process.env.REACT_APP_REFERRAL_BASE_URL || "https://wespeakspanish-cr.com";

const emptyForm = {
  name: "",
  email: "",
  whatsapp: "",
  code: "",
  commission_pct: 15,
  active: true,
  notes: "",
};

export default function Affiliates() {
  const { t, lang } = useI18n();
  const confirm = useConfirm();
  const [tab, setTab] = useState("affiliates"); // affiliates | commissions | resources
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [commissions, setCommissions] = useState({ items: [], totals: {} });
  const [commLoading, setCommLoading] = useState(false);
  const [filterAffiliate, setFilterAffiliate] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMonth, setFilterMonth] = useState(""); // YYYY-MM

  // ---- Marketing resources ----
  const [resources, setResources] = useState([]);
  const [resLoading, setResLoading] = useState(false);
  const [openResDialog, setOpenResDialog] = useState(false);
  const [resFile, setResFile] = useState(null);
  const [resTitle, setResTitle] = useState("");
  const [resUrl, setResUrl] = useState("");
  const [resUploading, setResUploading] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const imageSrc = (r) => `${backendUrl}${r.image_view_url}`;

  const loadResources = async () => {
    setResLoading(true);
    try {
      const r = await api.get("/affiliate-resources");
      setResources(r.data);
    } catch {
      toast.error(t("error_generic"));
    } finally {
      setResLoading(false);
    }
  };

  const submitResource = async () => {
    if (!resFile) { toast.error("Elegí una imagen"); return; }
    try {
      setResUploading(true);
      const fd = new FormData();
      fd.append("file", resFile);
      if (resTitle.trim()) fd.append("title", resTitle.trim());
      if (resUrl.trim()) fd.append("destination_url", resUrl.trim());
      await api.post("/affiliate-resources", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Imagen subida");
      setOpenResDialog(false);
      setResFile(null); setResTitle(""); setResUrl("");
      loadResources();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setResUploading(false);
    }
  };

  const toggleResourceActive = async (r) => {
    try {
      await api.patch(`/affiliate-resources/${r.id}`, { active: !r.active });
      loadResources();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const deleteResource = async (r) => {
    const ok = await confirm({
      title: "¿Borrar imagen?",
      description: `Se eliminará "${r.title || 'Sin título'}". Los afiliados ya no la verán.`,
      confirmText: "Sí, borrar",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/affiliate-resources/${r.id}`);
      toast.success(t("deleted"));
      loadResources();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const loadAffiliates = async () => {
    setLoading(true);
    try {
      const r = await api.get("/affiliates");
      setAffiliates(r.data);
    } catch {
      toast.error(t("error_generic"));
    } finally {
      setLoading(false);
    }
  };

  const loadCommissions = async () => {
    setCommLoading(true);
    try {
      const params = {};
      if (filterAffiliate !== "all") params.affiliate_id = filterAffiliate;
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterMonth) params.month = filterMonth;
      const r = await api.get("/affiliate-commissions", { params });
      setCommissions(r.data);
    } catch {
      toast.error(t("error_generic"));
    } finally {
      setCommLoading(false);
    }
  };

  useEffect(() => { loadAffiliates(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (tab === "commissions") loadCommissions(); /* eslint-disable-next-line */ }, [tab, filterAffiliate, filterStatus, filterMonth]);
  useEffect(() => { if (tab === "resources") loadResources(); /* eslint-disable-next-line */ }, [tab]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpenDialog(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name || "",
      email: a.email || "",
      whatsapp: a.whatsapp || "",
      code: a.code || "",
      commission_pct: a.commission_pct ?? 15,
      active: !!a.active,
      notes: a.notes || "",
    });
    setOpenDialog(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        code: form.code.trim() || null,
        commission_pct: Number(form.commission_pct) || 0,
        active: !!form.active,
        notes: form.notes.trim() || null,
      };
      if (editing) await api.patch(`/affiliates/${editing.id}`, payload);
      else {
        const r = await api.post("/affiliates", payload);
        if (r.data?.email && r.data?.user_id) {
          toast.success(`Afiliado creado. Se envió una invitación a ${r.data.email}`);
        } else {
          toast.success(t("saved"));
        }
      }
      if (editing) toast.success(t("saved"));
      setOpenDialog(false);
      loadAffiliates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/affiliates/${a.id}`, { active: !a.active });
      loadAffiliates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const removeAffiliate = async (a) => {
    const ok = await confirm({
      title: "¿Borrar afiliado?",
      description: `Se eliminará "${a.name}". Las comisiones pendientes serán descartadas. Comisiones YA PAGADAS bloquean el borrado (marca inactivo en su lugar).`,
      confirmText: "Sí, borrar",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/affiliates/${a.id}`);
      toast.success(t("deleted"));
      loadAffiliates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const resendInvite = async (a) => {
    if (!a.email) { toast.error("El afiliado no tiene email — agregalo primero"); return; }
    try {
      const r = await api.post(`/affiliates/${a.id}/resend-invite`);
      toast.success(`Invitación enviada a ${r.data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const refLink = (code) => `${REF_BASE}/?ref=${encodeURIComponent(code)}`;

  const copyLink = async (code) => {
    try {
      await navigator.clipboard.writeText(refLink(code));
      toast.success("Link copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const markCommissionPaid = async (c) => {
    try {
      await api.patch(`/affiliate-commissions/${c.id}`, { status: "paid" });
      toast.success("Comisión marcada como pagada");
      loadCommissions();
      loadAffiliates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const revertCommission = async (c, label) => {
    const ok = await confirm({
      title: label === "revert" ? "¿Reversar pago de comisión?" : "¿Restaurar comisión?",
      description: label === "revert"
        ? `La comisión de $${c.commission_amount.toFixed(2)} de ${c.affiliate_name} volverá a estado Pendiente (se borra la fecha de pago).`
        : `La comisión de $${c.commission_amount.toFixed(2)} de ${c.affiliate_name} volverá a estado Pendiente.`,
      confirmText: "Sí, continuar",
    });
    if (!ok) return;
    try {
      await api.patch(`/affiliate-commissions/${c.id}`, { status: "pending" });
      toast.success("Comisión devuelta a pendiente");
      loadCommissions();
      loadAffiliates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const deleteCommission = async (c) => {
    const ok = await confirm({
      title: "¿Eliminar comisión definitivamente?",
      description: `La comisión de $${c.commission_amount.toFixed(2)} de ${c.affiliate_name} por ${c.student_name || "—"} se borrará del historial de forma permanente. Esta acción no se puede deshacer.`,
      confirmText: "Sí, eliminar",
    });
    if (!ok) return;
    try {
      await api.delete(`/affiliate-commissions/${c.id}`);
      toast.success("Comisión eliminada definitivamente");
      loadCommissions();
      loadAffiliates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const cancelCommission = async (c) => {
    const ok = await confirm({
      title: "¿Cancelar comisión?",
      description: `La comisión de $${c.commission_amount.toFixed(2)} de ${c.affiliate_name} por ${c.student_name} quedará marcada como cancelada.`,
      confirmText: "Sí, cancelar",
    });
    if (!ok) return;
    try {
      await api.patch(`/affiliate-commissions/${c.id}`, { status: "cancelled" });
      toast.success("Comisión cancelada");
      loadCommissions();
      loadAffiliates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filterAffiliate !== "all") params.set("affiliate_id", filterAffiliate);
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterMonth) params.set("month", filterMonth);
      const url = `/affiliate-commissions/export?${params.toString()}`;
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const dl = document.createElement("a");
      dl.href = URL.createObjectURL(blob);
      dl.download = `comisiones_${filterMonth || currentMonthISO()}.csv`;
      document.body.appendChild(dl); dl.click(); dl.remove();
    } catch {
      toast.error(t("error_generic"));
    }
  };

  const totals = commissions.totals || {};
  const activeCount = useMemo(() => affiliates.filter((a) => a.active).length, [affiliates]);
  const totalPending = useMemo(() => affiliates.reduce((s, a) => s + (a.pending_total || 0), 0), [affiliates]);
  const totalPaid = useMemo(() => affiliates.reduce((s, a) => s + (a.paid_total || 0), 0), [affiliates]);

  return (
    <div className="space-y-6" data-testid="affiliates-page">
      <PageHeader
        title={t("affiliates")}
        action={
          tab === "affiliates" ? (
            <Button onClick={openCreate} data-testid="new-affiliate-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Nuevo afiliado
            </Button>
          ) : tab === "resources" ? (
            <Button onClick={() => setOpenResDialog(true)} data-testid="new-resource-btn">
              <Upload className="h-4 w-4 mr-1.5" /> Subir imagen
            </Button>
          ) : null
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-4 border-l-4 border-l-brand-blue">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Afiliados activos</div>
              <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="affiliates-active-count">{activeCount}</div>
            </div>
            <Users className="h-8 w-8 text-brand-blue opacity-60" />
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-amber-500">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Comisiones pendientes</div>
              <div className="text-2xl font-bold text-amber-600 mt-1" data-testid="affiliates-pending-total">${totalPending.toFixed(2)}</div>
            </div>
            <DollarSign className="h-8 w-8 text-amber-500 opacity-60" />
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-emerald-500">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Comisiones pagadas</div>
              <div className="text-2xl font-bold text-emerald-600 mt-1" data-testid="affiliates-paid-total">${totalPaid.toFixed(2)}</div>
            </div>
            <CheckCircle2 className="h-8 w-8 text-emerald-500 opacity-60" />
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        <button
          data-testid="tab-affiliates"
          onClick={() => setTab("affiliates")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "affiliates"
              ? "border-brand-blue text-brand-blue"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Afiliados ({affiliates.length})
        </button>
        <button
          data-testid="tab-commissions"
          onClick={() => setTab("commissions")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "commissions"
              ? "border-brand-blue text-brand-blue"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Comisiones
        </button>
        <button
          data-testid="tab-resources"
          onClick={() => setTab("resources")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "resources"
              ? "border-brand-blue text-brand-blue"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Páginas para compartir
        </button>
      </div>

      {/* Affiliates tab */}
      {tab === "affiliates" && (
        <div className="space-y-2" data-testid="affiliates-list">
          {loading && <p className="text-sm text-slate-500">Cargando…</p>}
          {!loading && affiliates.length === 0 && (
            <Card className="p-8 text-center">
              <Handshake className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 mb-3">Aún no tenés afiliados registrados.</p>
              <Button onClick={openCreate}>Crear tu primer afiliado</Button>
            </Card>
          )}
          {affiliates.map((a) => (
            <Card key={a.id} className="p-4" data-testid={`affiliate-row-${a.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-slate-900">{a.name}</h3>
                    <Badge variant="outline" className="font-mono text-xs">{a.code}</Badge>
                    {a.active ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Activo</Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-500 border-slate-200">Inactivo</Badge>
                    )}
                    <Badge variant="outline" className="text-xs">{a.commission_pct}%</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {a.email && <span>{a.email}</span>}
                    {a.email && a.whatsapp && <span> · </span>}
                    {a.whatsapp && <span>{a.whatsapp}</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                    <span><Users className="inline h-3 w-3 mr-0.5" /> {a.lead_count || 0} leads</span>
                    <span className="text-amber-600">
                      <DollarSign className="inline h-3 w-3" /> {a.pending_count || 0} pendientes (${(a.pending_total || 0).toFixed(2)})
                    </span>
                    <span className="text-emerald-600">
                      <CheckCircle2 className="inline h-3 w-3" /> {a.paid_count || 0} pagadas (${(a.paid_total || 0).toFixed(2)})
                    </span>
                  </div>
                  {a.notes && (
                    <p className="text-xs text-slate-500 italic mt-1">{a.notes}</p>
                  )}
                  {(a.payout_iban || a.payout_sinpe) ? (
                    <div className="text-xs text-slate-600 mt-1 flex items-center gap-3 flex-wrap" data-testid={`payout-info-${a.id}`}>
                      {a.payout_iban && (
                        <span className="font-mono">{a.payout_bank ? `${a.payout_bank} · ` : ""}{a.payout_iban}</span>
                      )}
                      {a.payout_sinpe && <span>SINPE: {a.payout_sinpe}</span>}
                      {a.payout_holder && <span>Titular: {a.payout_holder}{a.payout_holder_id ? ` (${a.payout_holder_id})` : ""}</span>}
                    </div>
                  ) : (
                    <p className="text-xs text-amber-600 mt-1" data-testid={`payout-missing-${a.id}`}>Sin datos de depósito aún</p>
                  )}
                  {a.promo_plan && (
                    <p className="text-xs text-slate-500 mt-1">Promoción: {a.promo_plan}{a.promo_urls ? ` — ${a.promo_urls}` : ""}</p>
                  )}
                  <div className="mt-2 flex items-center gap-1.5 text-xs">
                    <code className="bg-slate-100 px-2 py-1 rounded text-slate-700 font-mono" data-testid={`ref-link-${a.id}`}>
                      {refLink(a.code)}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2"
                      onClick={() => copyLink(a.code)}
                      data-testid={`copy-link-${a.id}`}
                      title="Copiar link"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                    <a
                      href={refLink(a.code)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-blue hover:underline inline-flex items-center gap-1"
                      title="Abrir en nueva pestaña"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Switch
                    checked={!!a.active}
                    onCheckedChange={() => toggleActive(a)}
                    data-testid={`active-switch-${a.id}`}
                  />
                  {a.email && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => resendInvite(a)}
                      className="text-brand-blue"
                      data-testid={`resend-invite-${a.id}`}
                      title="Reenviar invitación por email"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(a)}
                    data-testid={`edit-affiliate-${a.id}`}
                    title="Editar"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600"
                    onClick={() => removeAffiliate(a)}
                    data-testid={`delete-affiliate-${a.id}`}
                    title="Borrar"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Commissions tab */}
      {tab === "commissions" && (
        <div className="space-y-3" data-testid="commissions-panel">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[180px]">
              <Label className="text-xs">Afiliado</Label>
              <Select value={filterAffiliate} onValueChange={setFilterAffiliate}>
                <SelectTrigger data-testid="filter-affiliate"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los afiliados</SelectItem>
                  {affiliates.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name} ({a.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs">Estado</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="paid">Pagadas</SelectItem>
                  <SelectItem value="cancelled">Canceladas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Mes</Label>
              <Input
                type="month"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                data-testid="filter-month"
                className="w-[160px]"
              />
            </div>
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-1.5" /> Exportar CSV
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
            <div className="p-3 bg-amber-50 rounded border border-amber-200">
              <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Pendientes</div>
              <div className="text-lg font-bold text-amber-700">${(totals.pending_total || 0).toFixed(2)} <span className="text-xs font-normal">({totals.pending_count || 0})</span></div>
            </div>
            <div className="p-3 bg-emerald-50 rounded border border-emerald-200">
              <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Pagadas</div>
              <div className="text-lg font-bold text-emerald-700">${(totals.paid_total || 0).toFixed(2)} <span className="text-xs font-normal">({totals.paid_count || 0})</span></div>
            </div>
            <div className="p-3 bg-slate-50 rounded border border-slate-200">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Canceladas</div>
              <div className="text-lg font-bold text-slate-500">{totals.cancelled_count || 0}</div>
            </div>
          </div>

          <div className="rounded-md border border-slate-200 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Fecha</th>
                  <th className="text-left px-3 py-2 font-medium">Afiliado</th>
                  <th className="text-left px-3 py-2 font-medium">Estudiante</th>
                  <th className="text-left px-3 py-2 font-medium">Programa</th>
                  <th className="text-right px-3 py-2 font-medium">Pago</th>
                  <th className="text-right px-3 py-2 font-medium">%</th>
                  <th className="text-right px-3 py-2 font-medium">Comisión</th>
                  <th className="text-center px-3 py-2 font-medium">Estado</th>
                  <th className="text-right px-3 py-2 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {commLoading && (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">Cargando…</td></tr>
                )}
                {!commLoading && commissions.items?.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">Sin comisiones para el filtro seleccionado.</td></tr>
                )}
                {commissions.items?.map((c) => (
                  <tr key={c.id} data-testid={`commission-row-${c.id}`}>
                    <td className="px-3 py-2 text-slate-700">{formatDMY((c.created_at || "").slice(0, 10), lang)}</td>
                    <td className="px-3 py-2">
                      <span className="text-slate-900">{c.affiliate_name}</span>
                      <span className="text-slate-400 text-xs ml-1">({c.affiliate_code})</span>
                    </td>
                    <td className="px-3 py-2 text-slate-700">{c.student_name || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{c.program_name || "—"}</td>
                    <td className="px-3 py-2 text-right text-slate-700 font-mono">${c.amount_paid.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-slate-500 font-mono">{c.commission_pct}%</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900">${c.commission_amount.toFixed(2)}</td>
                    <td className="px-3 py-2 text-center">
                      {c.status === "pending" && <Badge className="bg-amber-100 text-amber-700 border-amber-200">Pendiente</Badge>}
                      {c.status === "paid" && (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                          Pagada {c.paid_at ? `· ${formatDMY(c.paid_at, lang)}` : ""}
                        </Badge>
                      )}
                      {c.status === "cancelled" && <Badge className="bg-slate-100 text-slate-500 border-slate-200">Cancelada</Badge>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {c.status === "pending" && (
                        <div className="inline-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => markCommissionPaid(c)}
                            data-testid={`mark-paid-${c.id}`}
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Pagar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs text-slate-500"
                            onClick={() => cancelCommission(c)}
                            data-testid={`cancel-comm-${c.id}`}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      {c.status === "paid" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-amber-700 hover:bg-amber-50"
                          onClick={() => revertCommission(c, "revert")}
                          data-testid={`revert-comm-${c.id}`}
                          title="Reversar pago (vuelve a Pendiente)"
                        >
                          <RotateCcw className="h-3 w-3 mr-1" /> Reversar
                        </Button>
                      )}
                      {c.status === "cancelled" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-slate-500"
                          onClick={() => revertCommission(c, "restore")}
                          data-testid={`restore-comm-${c.id}`}
                          title="Restaurar (vuelve a Pendiente)"
                        >
                          <RotateCcw className="h-3 w-3 mr-1" /> Restaurar
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs text-red-500 hover:bg-red-50 hover:text-red-600"
                        onClick={() => deleteCommission(c)}
                        data-testid={`delete-comm-${c.id}`}
                        title="Eliminar definitivamente"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Resources tab */}
      {tab === "resources" && (
        <div className="space-y-3" data-testid="resources-panel">
          <p className="text-sm text-slate-600">
            Estas imágenes se muestran a todos los afiliados en su portal. Cada afiliado ve la imagen
            y puede copiar un link que incluye su código automáticamente para poder rastrear clics.
            Si dejás la URL vacía, se usa el link principal del sitio como destino.
          </p>
          {resLoading && <p className="text-sm text-slate-500">Cargando…</p>}
          {!resLoading && resources.length === 0 && (
            <Card className="p-8 text-center">
              <ImageIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 mb-3">Todavía no cargaste imágenes.</p>
              <Button onClick={() => setOpenResDialog(true)} data-testid="empty-upload-btn">
                <Upload className="h-4 w-4 mr-1.5" /> Subir la primera
              </Button>
            </Card>
          )}
          {!resLoading && resources.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="resources-grid">
              {resources.map((r) => (
                <Card key={r.id} className="overflow-hidden flex flex-col" data-testid={`resource-card-${r.id}`}>
                  <div className="relative aspect-video bg-slate-100">
                    <img
                      src={imageSrc(r)}
                      alt={r.title || "Recurso"}
                      className="absolute inset-0 h-full w-full object-cover"
                      loading="lazy"
                    />
                    {!r.active && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Badge className="bg-slate-700 text-white border-none">Inactiva</Badge>
                      </div>
                    )}
                  </div>
                  <div className="p-3 flex-1 flex flex-col gap-2">
                    <div className="font-medium text-slate-900 text-sm truncate">{r.title || <em className="text-slate-400 not-italic">Sin título</em>}</div>
                    <div className="text-[11px] text-slate-500 truncate" title={r.destination_url || "Link principal"}>
                      {r.destination_url ? (
                        <a href={r.destination_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                          {r.destination_url}
                        </a>
                      ) : (
                        <em className="not-italic">→ Link principal (por defecto)</em>
                      )}
                    </div>
                    <div className="mt-auto flex items-center justify-between pt-2 border-t border-slate-100">
                      <div className="flex items-center gap-1">
                        <Switch
                          checked={!!r.active}
                          onCheckedChange={() => toggleResourceActive(r)}
                          data-testid={`resource-active-${r.id}`}
                        />
                        <span className="text-[10px] text-slate-500">{r.active ? "Activa" : "Oculta"}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 h-7 px-2"
                        onClick={() => deleteResource(r)}
                        data-testid={`resource-delete-${r.id}`}
                        title="Borrar"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upload resource dialog */}
      <Dialog open={openResDialog} onOpenChange={(o) => { setOpenResDialog(o); if (!o) { setResFile(null); setResTitle(""); setResUrl(""); } }}>
        <DialogContent data-testid="resource-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-brand-blue" /> Subir imagen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Imagen *</Label>
              <Input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setResFile(e.target.files?.[0] || null)}
                data-testid="resource-file-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">JPG, PNG o WebP. Máx 5 MB.</p>
              {resFile && (
                <div className="mt-2 rounded-md border border-slate-200 overflow-hidden">
                  <img src={URL.createObjectURL(resFile)} alt="preview" className="w-full max-h-48 object-cover" />
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Nombre (opcional)</Label>
              <Input
                value={resTitle}
                onChange={(e) => setResTitle(e.target.value)}
                data-testid="resource-title-input"
                placeholder="Ej. Sede Atenas"
              />
            </div>
            <div>
              <Label className="text-xs">URL de destino (opcional)</Label>
              <Input
                value={resUrl}
                onChange={(e) => setResUrl(e.target.value)}
                data-testid="resource-url-input"
                placeholder="https://wespeakspanish-cr.com/sedes/atenas"
              />
              <p className="text-[11px] text-slate-500 mt-1">Si lo dejás vacío, usamos el link principal del sitio.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenResDialog(false)} data-testid="resource-cancel-btn">{t("cancel")}</Button>
            <Button onClick={submitResource} disabled={resUploading || !resFile} data-testid="resource-submit-btn">
              {resUploading ? "Subiendo…" : "Subir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create / Edit affiliate dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent data-testid="affiliate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Handshake className="h-5 w-5 text-brand-blue" />
              {editing ? "Editar afiliado" : "Nuevo afiliado"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nombre completo *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="affiliate-name-input"
                placeholder="María López"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="affiliate-email-input"
                  placeholder="maria@ejemplo.com"
                />
              </div>
              <div>
                <Label className="text-xs">WhatsApp</Label>
                <Input
                  value={form.whatsapp}
                  onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                  data-testid="affiliate-whatsapp-input"
                  placeholder="+506 8888 8888"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Código de referencia</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  data-testid="affiliate-code-input"
                  placeholder="MARIA23 (opcional — se genera)"
                  className="font-mono uppercase"
                />
                <p className="text-[11px] text-slate-500 mt-1">Solo letras y números. Se auto-genera si lo dejás vacío.</p>
              </div>
              <div>
                <Label className="text-xs">% de comisión *</Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={form.commission_pct}
                    onChange={(e) => setForm({ ...form, commission_pct: e.target.value })}
                    data-testid="affiliate-pct-input"
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">%</span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Se aplica al primer pago del estudiante referido.</p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-200">
              <div>
                <Label htmlFor="affiliate-active-switch" className="text-xs">Activo</Label>
                <p className="text-[11px] text-slate-500">Solo los afiliados activos generan comisiones nuevas.</p>
              </div>
              <Switch
                id="affiliate-active-switch"
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="affiliate-active-switch"
              />
            </div>
            <div>
              <Label className="text-xs">Notas internas</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="affiliate-notes-input"
                placeholder="Notas internas (opcional)"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)} data-testid="affiliate-cancel-btn">{t("cancel")}</Button>
            <Button onClick={submit} data-testid="affiliate-submit-btn">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
